import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { KeyValue } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { WhiteLabelBlotterTrade } from '@approot/models/white-label-blotter';
import {
  AgGridService,
  ConfigService,
  TradesCaptureService
} from '@approot/services';
import { DateUtilities, SubscriptionSink } from '@approot/utilities';
import {
  ColumnApi,
  GridApi,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import dayjs from 'dayjs';
import { Throttle } from 'lodash-decorators';
import { BackToBackService } from './back-to-back-trades/back-to-back-trades.service';
import { DealDetailsGraphComponent } from './deal-details-graph/deal-details-graph.component';
import { RequestIdCellRendererComponent } from './request-id-cell-renderer/request-id-cell-renderer.component';

@Component({
  selector: 'app-white-label-blotter',
  templateUrl: './white-label-blotter.component.html',
  styleUrls: ['./white-label-blotter.component.scss']
})
export class WhiteLabelBlotterComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild(DealDetailsGraphComponent, { static: false })
  public dealDetailsGraphComponent: DealDetailsGraphComponent;
  public selectedRequestId: number | null = null;
  public columnDefs: Header[] = [];
  public sideBarDef: SideBarDef;
  public form: FormGroup;
  public gridApi: GridApi;
  public isDealDetailsOpen = false;
  public isViewButtonEnabled = false;
  public selectedTrade: WhiteLabelBlotterTrade;
  private lastUpdatedAt = new Date();
  private subSink = new SubscriptionSink();
  private columnApi: ColumnApi;
  private backToBackService = inject(BackToBackService);

  constructor(
    private tradesCaptureService: TradesCaptureService,
    private configService: ConfigService,
    private agGridService: AgGridService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  public ngAfterViewInit(): void {
    const currentDate = dayjs().format('D MMM YYYY');
    this.setDate(currentDate);
    this.getRequestsStatus();
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public onSelectionChanged(): void {
    this.selectedTrade = this.gridApi.getSelectedRows()[0];
  }

  public ngOnInit(): void {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subSink.sink = this.configService.getUserSettings().subscribe(x => {
      const headers = x.ag_grid_config.find(
        y => y.identifier === 'defaultWhiteLabelBlotterHeaders'
      )?.headers as string[];
      this.columnDefs = this.agGridService.formatHeaders([
        {
          headerName: '',
          field: 'checkbox',
          checkboxSelection: true,
          width: 40,
          headerCheckboxSelection: false,
          hide: false,
          filter: false,
          resizable: false,
          sortable: false
        }
      ]);

      const requestIdCell = this.agGridService.headers
        .filter(header => headers.includes(header.field))
        .map(columnDef => {
          if (columnDef.field === 'requestId') {
            return {
              ...columnDef,
              cellRenderer: RequestIdCellRendererComponent
            };
          } else {
            return columnDef;
          }
        });

      if (headers) {
        this.columnDefs = [
          {
            headerName: 'Select',
            field: 'checkbox',
            checkboxSelection: true,
            width: 40,
            headerCheckboxSelection: false,
            hide: false,
            filter: false,
            resizable: false,
            sortable: false
          },
          ...requestIdCell
        ];
      }
    });

    const currentDate = dayjs().format('D MMM YYYY');
    this.form = new FormGroup({
      date: new FormControl(currentDate, this.dateValidator)
    });

    const dateCtrl = this.form.get('date');
    dateCtrl.markAsTouched(); // prevent weird blanking of date when untouched for the first time
    this.subSink.sink = dateCtrl.valueChanges.subscribe(date => {
      if (/^(\d\d)\/(\d\d)\/(\d\d\d\d)$/.exec(date)) {
        const date2 = dayjs(`${RegExp.$3}-${RegExp.$2}-${RegExp.$1}`).format(
          'D MMM YYYY'
        );

        setTimeout(() => {
          dateCtrl.setValue(date2);
        }, 0);
      } else {
        this.getRequestsStatus();
      }
    });
  }

  public onViewButtonClick(): void {
    this.isDealDetailsOpen = true;
  }

  public closeContainer(): void {
    this.isDealDetailsOpen = false;
  }

  public getRequestsStatus(): void {
    const dateCtrl = this.form.get('date');
    if (!dateCtrl?.valid) {
      return;
    }

    let date = dateCtrl.value;
    date = DateUtilities.prettyDateToDate(date);

    this.gridApi?.showLoadingOverlay();
    this.tradesCaptureService
      .getRequestsStatus(date)
      .subscribe(statusResponse => {
        this.lastUpdatedAt = new Date();
        this.gridApi.setRowData(statusResponse.response.result);
        this.resizeColumns();
      });
  }

  @Throttle(1_000)
  public get lastUpdatedAtStr(): string {
    return dayjs(this.lastUpdatedAt).fromNow();
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    this.gridApi.setRowData([]);
    this.gridApi.sizeColumnsToFit();
  }

  public displayBackToBackBlotter(): void {
    this.backToBackService.displayBackToBackBlotter();
  }

  public displayBackToBackGrid(): void {
    this.backToBackService.displayBackToBackGrid();
  }

  private resizeColumns(skipHeader = false): void {
    const colIds = this.columnApi
      .getAllColumns()
      .map(column => column.getColId());
    this.columnApi.autoSizeColumns(colIds, skipHeader);
  }

  private dateValidator(control: AbstractControl): KeyValue | null {
    const date = control.value;
    if (DateUtilities.isPrettyDate(date)) {
      return null;
    }
    return { invalidEntry: { value: date } };
  }

  private setDate(dateValue: string): void {
    // clarity design date has issues setting the value on creation
    let dateEl =
      this.elementRef.nativeElement.querySelector('.input-date input');
    if (dateEl) {
      this.renderer.setProperty(dateEl, 'value', dateValue);
    }
  }
}
